import { initApplicationInstance } from './init/core/Application';
import initHeader from './init/startup/header';
import initNewsletterPopup from './init/startup/newsletterPopup';

// Literal import paths let esbuild create independently cached page chunks.
const pageLoaders = {
    homePage: () => import('./page-scripts/homePage'),
    landingPage: () => import('./page-scripts/landingPage'),
    whyBeautyFormulasPage: () => import('./page-scripts/whyBeautyFormulasPage'),
    aboutPage: () => import('./page-scripts/aboutPage'),
    getStartedPage: () => import('./page-scripts/getStartedPage'),
    getStartedAltPage: () => import('./page-scripts/getStartedAltPage'),
    creatorCommunityPage: () => import('./page-scripts/creatorCommunityPage'),
    tradeEnquiryStrips: () => import('./page-scripts/tradeEnquiryStrips'),
    productPage: () => import('./page-scripts/productPage'),
    shopArchive: () => import('./page-scripts/shopArchive'),
    fixCheckoutAccessibility: () => import('./utils/fixCheckoutAccessibility'),
};

type PageScriptName = keyof typeof pageLoaders;

const animatedPages = new Set<PageScriptName>([
    'homePage',
    'landingPage',
    'whyBeautyFormulasPage',
    'aboutPage',
    'getStartedPage',
    'creatorCommunityPage',
    'tradeEnquiryStrips',
]);

function getCurrentPageScripts(): PageScriptName[] {
    const scripts = new Set<PageScriptName>();
    const page = document.body.dataset.pageScript;

    if (page && Object.prototype.hasOwnProperty.call(pageLoaders, page)) {
        scripts.add(page as PageScriptName);
    }

    // Also support catalogue/product components embedded outside their usual routes.
    if (document.querySelector('.single-product')) scripts.add('productPage');
    if (document.querySelector('[data-clarity-shop-catalog]')) scripts.add('shopArchive');
    if (document.body.classList.contains('woocommerce-checkout')) {
        scripts.add('fixCheckoutAccessibility');
    }

    return [...scripts];
}

async function startApplication() {
    initApplicationInstance();
    initHeader();

    const fontsReady = document.fonts.ready;
    // Global UI must remain usable even if a page chunk fails to download.
    void fontsReady.then(initNewsletterPopup).catch((error) => {
        console.error('Newsletter initialization failed', error);
    });

    const scripts = getCurrentPageScripts();
    const hasAnimFrom = Boolean(
        document.querySelector(
            '.anim-from-top-target, .anim-from-right-target, .anim-from-bottom-target, .anim-from-left-target'
        )
    );
    const needsGSAP = hasAnimFrom || scripts.some((script) => animatedPages.has(script));

    const gsapReady = needsGSAP
        ? import('./init/startup/register_gsap_plugin').then((module) => module.default())
        : Promise.resolve();
    const animations = hasAnimFrom ? import('./init/animations/animFrom') : Promise.resolve(null);

    const pageModules = scripts.map(async (script) => {
        try {
            const module = await pageLoaders[script]();
            if (script === 'fixCheckoutAccessibility') {
                module.default();
                return null;
            }
            return { script, module };
        } catch (error) {
            console.error(`Page module failed: ${script}`, error);
            return null;
        }
    });

    // Download in parallel; preserve Application -> GSAP -> animations -> page order.
    const [modules, animationModule] = await Promise.all([
        Promise.all(pageModules),
        animations,
        gsapReady,
        fontsReady,
    ]);
    animationModule?.default();

    for (const entry of modules) {
        if (!entry) continue;
        try {
            entry.module.default();
        } catch (error) {
            console.error(`Page initialization failed: ${entry.script}`, error);
        }
    }
}

function start() {
    void startApplication().catch((error) => console.error('Application startup failed', error));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
    start();
}
